<template>
  <div class="reservations">
    <SidebarLayout3
      :breadcrumb="breadcrumb"
      :rootPath="rootPath"
      :pageTitle="pageTitle"
      class="reservations--sidebar"
    >
      <!-- BARRA LATERAL -->
      <template #sidebar class="sidebar">
        <!-- AÇÕES DA PÁGINA -->
        <SidebarBlock class="page-actions">
          <PmwHeading
            class="sidebar--title"
            size="paragraph-80"
            weight="weight-medium"
            >Ações da página</PmwHeading
          >
          <!-- SALVAR -->
          <Button
            :disabled="validateRoom || isLoading"
            tooltipText="Para salvar, preencha os campos obrigatórios sinalizados com *"
            class="sidebar--button"
            @click="roomId === null ? createRoom() : saveRoom()"
          >
            Salvar
          </Button>
          <!-- DESATIVAR -->
          <Button
            v-if="status"
            variation="error"
            class="sidebar--button"
            @click="showAlertModalInactivate = true"
          >
            Desativar
          </Button>
          <Button
            v-else
            class="sidebar--button"
            @click="showAlertModalActivate = true"
          >
            Ativar
          </Button>
          <!-- VOLTAR -->
          <Button
            :link="`/hoteis/quartos/${hotelId}`"
            layout="outline"
            element="router-link"
            class="sidebar--button"
          >
            Voltar sem salvar
          </Button>
        </SidebarBlock>
      </template>
      <template #content>
        <Tabs class="tabs">
          <template
            v-for="(tab, index) in tabs"
            v-slot:[`tab-title-${index}`]="{ title, tabObj }"
          >
            <span :key="tab.title" class="tab-title">
              <img :src="tabObj.extraData.flag" />
              {{ title }}
            </span>
          </template>
          <Tab
            v-for="tab in tabs"
            :extraData="tab"
            :key="tab.title"
            :title="tab.title"
          >
            <div>
              <!-- NOME -->
              <div class="form-name-row">
                <FormInput
                  v-model="name[tab.dataAttribute].value"
                  :label="'Nome (' + tab.label + ')'"
                  :maxlength="50"
                  :required="tab.dataAttribute === 'pt-BR'"
                  :title="tab.title"
                  :showCounter="true"
                  placeholder="Digite o nome do quarto"
                  class="form-name"
                />

                <InputCheckbox
                  v-model="exclusivoCentralReservas"
                  :checked="exclusivoCentralReservas"
                  label="Uso exclusivo da Central de Reservas"
                  class="form-name-checkbox"
                />
              </div>

              <!-- DESCRIÇÃO -->
              <Textarea
                v-model="description[tab.dataAttribute].value"
                :label="'Descrição (' + tab.label + ')'"
                :maxlength="500"
                :required="tab.dataAttribute === 'pt-BR'"
                :title="tab.title"
                :counter="true"
                placeholder="Digite a descrição do quarto"
                class="form-desc"
              ></Textarea>
              <!-- SEPARADOR -->
              <SeparateLine :top="60" :bottom="30" :right="0" :left="0" />
            </div>
          </Tab>
        </Tabs>

        <!-- CONFIGURAÇÕES -->
        <PmwHeading
          element="h3"
          size="size-large"
          weight="weight-light"
          class="title"
          >Configurações do quarto</PmwHeading
        >
        <div class="form-grid">
          <!-- BASE DO QUARTO - VISIVEL SE O HOTEL FOR FLUTUANTE -->
          <FormInput
            v-if="!isAbsolute"
            v-model="baseRoom"
            :infoIcon="true"
            :required="true"
            label="Base do quarto"
            type="number"
            placeholder="00"
            infoText="A base do quarto se refere a valor. Ex: Se a base for 2, o valor será igual para até 2 hóspedes (adultos ou crianças). O hóspede pode reservar para 1 pessoa, mas o valor a ser pago é referente à base de 2 ocupantes."
            class="form-grid--item__base-room"
          />
          <!-- MÍNIMO DE ADULTOS -->
          <FormInput
            v-model="minAdults"
            :required="true"
            label="Mínimo de adultos"
            type="number"
            placeholder="00"
            class="form-grid--item__min-adults"
          />
          <!-- MÁXIMO DE ADULTOS -->
          <FormInput
            v-model="maxAdults"
            :required="true"
            label="Máximo de adultos"
            type="number"
            placeholder="00"
            class="form-grid--item__max-adults"
          />
          <!-- CRIANÇAS GRÁTIS -->
          <FormInput
            v-if="isAbsolute"
            v-model="freeChildren"
            :error="childrenError"
            :required="true"
            :showErrorFeedbackMessage="childrenError"
            label="Crianças grátis"
            type="number"
            placeholder="00"
            class="form-grid--item__free-children"
          >
            <template #feedback>
              Erro
              <PmwInfoTooltip class="form-grid--info-icon">
                <template #content>
                  O número de crianças grátis não pode ser maior do que o número
                  máximo de crianças.
                </template>
              </PmwInfoTooltip>
            </template>
          </FormInput>
          <!-- MÁXIMO DE CRIANÇAS -->
          <FormInput
            v-model="maxChildren"
            :required="true"
            label="Máximo de crianças"
            type="number"
            placeholder="00"
            class="form-grid--item__max-children"
          />
          <!-- MÁXIMO DE OCUPANTES -->
          <FormInput
            v-model="maxOccupants"
            :required="true"
            label="Máximo de ocupantes"
            type="number"
            placeholder="00"
            class="form-grid--item__max-occupants"
          />
        </div>

        <!-- SEPARADOR -->
        <SeparateLine :top="30" :bottom="30" :right="0" :left="0" />

        <!-- CONFIGURAÇÃO DE FAIXA ETÁRIA -->
        <AgeRangeConfig
          :enabled.sync="ageRangeEnabled"
          :ranges.sync="ageRanges"
          :paxCriancas="paxCriancas"
          :showTooltip="getIsAdmin"
          :disabled="Number(maxChildren) === 0"
        />

        <!-- SEPARADOR -->
        <SeparateLine :top="30" :bottom="30" :right="0" :left="0" />

        <!-- AMENIDADES -->
        <PmwHeading element="h3" size="size-large" weight="weight-light"
          >Amenidades</PmwHeading
        >
        <PmwParagraph size="paragraph-40" class="mb-2">
          Defina as amenidades que este quarto oferece.
        </PmwParagraph>
        <PmwParagraph size="paragraph-40">
          <b>Não encontrou a amenidade?</b>
          <router-link to="/amenidades" class="link ml-1"
            >Clique aqui para configurar</router-link
          >.
        </PmwParagraph>
        <!-- ADICIONAR AMENIDADES -->
        <PmwSelectionBox
          v-model="amenitiesRoom"
          :list="formattedAmenities"
          titleFirstBox="Adicionar Amenidades"
          selectionPlaceholder="Busque uma amenidade"
          titleSecondBox="Amenidades selecionadas"
          emptySelected="Nenhuma amenidade selecionada para este hotel."
          class="pmw-selection-box"
        ></PmwSelectionBox>
        <!-- SEPARADOR -->
        <SeparateLine :top="-1" :bottom="30" :right="0" :left="0" />
        <!-- IMAGEM MÚLTIPLAS -->
        <PmwImageUpload
          v-model="images"
          :multiple="true"
          :maxSize="1000"
          :imageWidth="840"
          :imageHeight="461"
          :maxImages="24"
          :fixedJpeg="true"
          alertMessage="Não redimensionada"
          maxImagesErrorText="Insira até 24 fotos para compor esta galeria."
          inputTextBox="Arraste as imagens para cá"
          inputTitle="Galeria de fotos"
          sizeErrorText="A imagem selecionada excede o limite de tamanho. Tente novamente com uma imagem menor que 1MB"
          titleDescription="Insira até 24 fotos para compor a galeria deste quarto."
          minSizeErrorText="A imagem precisa ter no mínimo a dimensão de 840 x 461 pixels. Tente novamente com uma imagem maior."
          inputButtonText="Upload do seu computador"
          sizeInfo="Dimensão recomendada 840 x 461"
          inputDescription="São aceitos arquivos .JPG e .PNG de até 1MB."
          class="pmw-image-upload"
          @croped="saveImageGallery($event)"
        />
        <!-- SEPARADOR -->
        <SeparateLine :top="40" :bottom="30" :right="0" :left="0" />
        <!-- AÇÕES -->
        <div class="form-actions">
          <!-- CANCELAR -->
          <Button
            :link="`/hoteis/quartos/${hotelId}`"
            element="router-link"
            layout="outline"
            size="large"
          >
            Voltar sem salvar
          </Button>
          <!-- SALVAR -->
          <Button
            :disabled="validateRoom || isLoading"
            tooltipText="Para salvar, preencha os campos obrigatórios sinalizados com *"
            size="large"
            @click="roomId === null ? createRoom() : saveRoom()"
          >
            Salvar
          </Button>
        </div>
        <!-- MODAL DE INATIVAÇÃO DE QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalInactivate"
          :closeButton="true"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          icon="alert"
          confirmButtonText="Sim, quero desativar"
          cancelButtonText="Não, Cancelar"
          @alert-modal-confirm="inactiveRooms"
          @alert-modal-cancel="showAlertModalInactivate = false"
        >
          <template #text>
            Ao desativar esse quarto, ele estará indisponível para venda no
            Letsbook. Você tem certeza que quer fazer isso?
          </template>
        </PmwAlertModal>
        <!-- MODAL DE ATIVAÇÃO DE QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalActivate"
          :closeButton="true"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          icon="alert"
          confirmButtonText="Sim, quero ativar"
          cancelButtonText="Não, Cancelar"
          @alert-modal-confirm="activeRooms"
          @alert-modal-cancel="showAlertModalActivate = false"
        >
          <template #text>
            Ao reativar esse quarto, ele estará disponível para venda no
            Letsbook. Você tem certeza que quer fazer isso?
          </template>
        </PmwAlertModal>
        <!-- MODAL DE ALERTA DE EXCLUSÃO DE QUARTO -->
        <PmwAlertModal
          v-model="showAlertModalRoomCantDelete"
          title="Atenção"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          confirmButtonText="Ok, entendi"
          cancelButtonText="Fechar"
          @alert-modal-confirm="showAlertModalRoomCantDelete = false"
          @alert-modal-cancel="showAlertModalRoomCantDelete = false"
        >
          <template #text>
            Houve um erro ao executar a ação.
          </template>
        </PmwAlertModal>
        <!-- MODAL: IMAGENS NÃO CORTADAS -->
        <PmwAlertModal
          v-model="showUncroppedImages"
          :buttonClose="true"
          title="Imagens não redimensionadas"
          cancelButtonColor="blue-100"
          confirmButtonColor="blue-100"
          icon="alert"
          confirmButtonText="Redimensionar"
          cancelButtonText="Cancelar"
          @alert-modal-confirm="showUncroppedImages = false"
          @alert-modal-cancel="showUncroppedImages = false"
        >
          <template #text>
            Algumas imagens não foram redimensionadas <br />
            e isso pode prejudicar a visualização.
          </template>
        </PmwAlertModal>
      </template>
    </SidebarLayout3>
  </div>
</template>

<script>
/** Bandeiras de idiomas */
import en from '@/assets/images/en-flag.png'
import es from '@/assets/images/es-flag.png'
import pt from '@/assets/images/ptbr-flag.png'

/** Componentes */
import SidebarLayout3 from '@/layouts/SidebarLayout3'
import { capitalizeString } from '@/helpers/string'
import { mapGetters } from 'vuex'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import Tab from '@/components/_base/patterns/Tab'
import Tabs from '@/components/_base/patterns/Tabs'
import FormInput from '@solucoes/components-lib-tmp/src/components/elements/FormInput'
import Textarea from '@solucoes/components-lib-tmp/src/components/elements/Textarea'
import SeparateLine from '@solucoes/components-lib-tmp/src/components/elements/SeparateLine'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph/PmwParagraph.vue'
import PmwSelectionBox from '@solucoes/components-lib-tmp/src/components/patterns/PmwSelectionBox'
import PmwImageUpload from '@solucoes/components-lib-tmp/src/components/patterns/PmwImageUpload'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import PmwInfoTooltip from '@solucoes/components-lib-tmp/src/components/patterns/PmwInfoTooltip'
import InputCheckbox from '@/components/_base/elements/InputCheckbox'
import AgeRangeConfig from '@/components/rooms/AgeRangeConfig'

export default {
  name: 'RoomsCreate',
  components: {
    SidebarLayout3,
    SidebarBlock,
    PmwHeading,
    FormInput,
    Textarea,
    Button,
    SeparateLine,
    Tab,
    Tabs,
    PmwParagraph,
    PmwSelectionBox,
    PmwImageUpload,
    PmwAlertModal,
    PmwInfoTooltip,
    InputCheckbox,
    AgeRangeConfig
  },
  data() {
    return {
      /** Id do quarto */
      roomId: null,
      /** Dados do quarto */
      room: null,
      /** Status do quarto */
      status: true,
      /** Id do hotel */
      hotelId: null,
      /** Configuração da página */
      pageTitle: 'Criar quarto',
      /** Caminhos da página */
      rootPath: {
        /** ponto de partida do breadcrumb  */
        label: 'Home',
        url: 'relatorio-conversao',
        isSpa: false /** se deve ser um link para o site novo ou antigo */
      },
      /** Caminho de pão */
      breadcrumb: [
        {
          label: 'Letsbook',
          url: '',
          isSpa: false
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true
        },
        {
          label: 'Quartos',
          url: `/hoteis/quartos/${this.$route.params.idHotel}`,
          isSpa: true
        },
        {
          label: 'Hotel',
          url: `/hoteis/editar/${this.$route.params.idHotel}`,
          isSpa: true
        },
        {
          label: 'Criar quarto',
          url: `/hoteis/quartos/${this.$route.params.idHotel}/novo`,
          isSpa: true
        }
      ],
      /** Abas com os idiomas */
      tabs: [
        {
          title: 'PT',
          label: 'Português',
          flag: pt,
          dataAttribute: 'pt-BR'
        },
        {
          title: 'EN',
          label: 'English',
          flag: en,
          dataAttribute: 'en-US'
        },
        {
          title: 'ES',
          label: 'Español',
          flag: es,
          dataAttribute: 'es-ES'
        }
      ],
      /** Input do nome */
      name: {
        'pt-BR': { value: '' },
        'en-US': { value: '' },
        'es-ES': { value: '' }
      },
      description: {
        'pt-BR': { value: '' },
        'en-US': { value: '' },
        'es-ES': { value: '' }
      },
      baseRoom: 1 /** Base do quarto */,
      minAdults: 1 /** Mínimo de adultos */,
      maxAdults: '' /** Máximo de adultos */,
      maxChildren: 0 /** Máximo de crianças */,
      maxOccupants: '' /** Máximo de ocupantes */,
      freeChildren: '' /** Número de crianças grátis */,
      exclusivoCentralReservas: false /** Uso exclusivo da Central de Reservas */,
      /** Configuração de faixa etária infantil */
      ageRangeEnabled: false,
      ageRanges: [],
      paxCriancas: [] /** Faixas etárias de crianças do hotel */,
      amenities: [] /** Lista de amenidades */,
      amenitiesRoom: [] /** Lista de amenidades selecionadas */,
      images: null /** Imagens do quarto */,
      /** Modal de desativação de quarto(s) */
      showAlertModalInactivate: false,
      /** Modal de ativação de quarto(s) */
      showAlertModalActivate: false,
      /** Modal de alerta das imagens não cortadas */
      showUncroppedImages: false,
      /** Modal de alerta de erro de alteração de status */
      showAlertModalRoomCantDelete: false,
      /** Se o hotel é absoluto ou não */
      isAbsolute: false,
      isLoading: false
    }
  },
  computed: {
    ...mapGetters('user', ['getIsAdmin']),

    childrenError() {
      if (this.freeChildren > this.maxChildren) {
        return true
      }

      return false
    },
    /** Trazer os dados em português */
    formattedAmenities() {
      const amenities = this.amenities.map((item) => {
        item.text = item.text.find((item) => item.idioma === 'pt-BR')?.nome
        return item
      })
      return amenities
    },
    /** Habilitar o botão de salvar quarto */
    validateRoom() {
      const baseInvalid = [
        this.name['pt-BR'].value,
        this.description['pt-BR'].value,
        this.isAbsolute === false ? this.baseRoom : false,
        this.minAdults,
        this.maxAdults,
        this.maxChildren,
        this.maxOccupants,
        this.isAbsolute === true ? this.freeChildren : 0
      ].includes(null || '')

      const ageRangeInvalid =
        this.ageRangeEnabled &&
        this.ageRanges.some((r) => r.limite === null || r.limite === '')

      return baseInvalid || ageRangeInvalid
    }
  },
  watch: {
    maxChildren(val) {
      if (Number(val) === 0) {
        this.ageRangeEnabled = false
        this.ageRanges = this.ageRanges.map((r) => ({ ...r, limite: null }))
      }
    }
  },
  async created() {
    /** Buscar amenidades */
    await this.getAmenities()
    /** Pegar o hotel primeiro para ter paxCriancas disponível */
    this.hotelId = this.$route.params.idHotel
    await this.getHotel()
    /** Pegar o quarto (depende de ageRanges populado pelo getHotel) */
    this.getRoom()
  },
  methods: {
    /** Buscar hotel */
    getHotel() {
      this.isLoading = true

      return this.$hotel
        .get(this.hotelId)
        .then((response) => {
          /** Se o hotel é absoluto ou não */
          this.isAbsolute = response.data.tarifarioAbsoluto

          /** Faixas etárias de crianças */
          this.paxCriancas = response.data.paxCriancas ?? []
          this.ageRanges = this.paxCriancas.map((pax) => ({
            label: pax.nome,
            codigoPax: pax.codigo,
            limite: null
          }))

          /** Caminho de pão */
          this.breadcrumb = [
            {
              label: 'Letsbook',
              url: '',
              isSpa: false
            },
            {
              label: 'Hotéis',
              url: '/hoteis',
              isSpa: true
            },
            {
              label: 'Quartos',
              url: `/hoteis/quartos/${this.$route.params.idHotel}`,
              isSpa: true
            },
            {
              label: capitalizeString(response.data.nome),
              url: `/hoteis/editar/${this.$route.params.idHotel}`,
              isSpa: true
            },
            {
              label: 'Criar quarto',
              url: `/hoteis/quartos/${this.$route.params.idHotel}/novo`,
              isSpa: true
            }
          ]
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar o hotel.'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /** Buscar quarto */
    getRoom() {
      this.isLoading = true

      this.$room
        .get(this.hotelId)
        .then((response) => {
          /** Pegar o quarto */
          if (this.$route.params.idRoom) {
            this.roomId = this.$route.params.idRoom
            this.room = response.data.find(
              (room) => parseInt(room.codigo) === parseInt(this.roomId)
            )
            /** Atualizar os dados da página */
            this.pageTitle = `Editando ${this.room.idiomas[0].nome ?? 'hotel'}`
            this.breadcrumb[3].label = `Editando ${this.room.idiomas[0].nome ??
              'hotel'}`
            /** Preencher dados */
            this.status = this.room.ativo
            this.baseRoom = this.room.baseOcupantes
            this.minAdults = this.room.minimoAdultos
            this.maxAdults = this.room.maximoAdultos
            this.maxChildren = this.room.maximoCriancas
            this.maxOccupants = this.room.maximoOcupantes
            this.freeChildren = this.room.quantidadeCriancasGratis
            this.exclusivoCentralReservas =
              this.room.exclusivoCentralReservas ?? false

            /** Preencher faixa etária */
            this.ageRangeEnabled = this.room.limiteFaixaEtariaAtivo ?? false
            if (this.room.limitesFaixaEtaria?.length) {
              this.ageRanges = this.ageRanges.map((range) => {
                const saved = this.room.limitesFaixaEtaria.find(
                  (f) => f.codigoPax === range.codigoPax
                )
                return saved ? { ...range, limite: saved.limite } : range
              })
            }

            this.amenitiesRoom = this.amenities.filter((item) =>
              this.room.amenidades.includes(item.id)
            )
            /** Preencher os campos do nome (pt, en, es) */
            for (let i = 0; i < this.room.idiomas.length; i++) {
              this.name[this.room.idiomas[i].idioma].value =
                this.room.idiomas[i].nome ?? ''
            }
            /** Preencher os campos da descricao (pt, en, es) */
            for (let i = 0; i < this.room.idiomas.length; i++) {
              this.description[this.room.idiomas[i].idioma].value =
                this.room.idiomas[i].descricao ?? ''
            }

            /** Imagens da galeria */
            this.images = this.room.galeria

            /** Verificar se a imagem principal está ou não na galeria */
            if (this.room.imagemPrincipal !== null) {
              if (!this.images.includes(this.room.imagemPrincipal)) {
                this.images.unshift(this.room.imagemPrincipal)
              }
            }
          }
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar o quarto.'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /** Formatar dados do quarto */
    formattedData() {
      /** Formatar os idiomas */
      const languages = [
        {
          idioma: 'pt-BR',
          nome: this.name['pt-BR'].value,
          descricao: this.description['pt-BR'].value
        },
        {
          idioma: 'en-US',
          nome: this.name['en-US'].value,
          descricao: this.description['en-US'].value
        },
        {
          idioma: 'es-ES',
          nome: this.name['es-ES'].value,
          descricao: this.description['es-ES'].value
        }
      ]

      /** Remover primeira imagem da galeria e adicionar como imagem principal */
      const gallery = [...this.images]
      const imagePrimary = gallery.length > 0 ? gallery[0] : null
      gallery.shift()

      const data = {
        codigoHotel: this.hotelId,
        nome: this.name['pt-BR'].value,
        minimoAdultos: this.minAdults,
        maximoAdultos: this.maxAdults,
        maximoCriancas: this.maxChildren,
        maximoOcupantes: this.maxOccupants,
        quantidadeCriancasGratis: this.isAbsolute ? this.freeChildren : 0,
        baseOcupantes: this.baseRoom,
        ativo: this.status,
        galeria: gallery,
        imagemPrincipal: imagePrimary,
        exclusivoCentralReservas: this.exclusivoCentralReservas,
        limiteFaixaEtariaAtivo: this.ageRangeEnabled,
        limitesFaixaEtaria: this.ageRanges.map((r) => ({
          codigoPax: r.codigoPax,
          limite: r.limite
        })),
        amenidades: this.amenitiesRoom.map((item) => item.id),
        idiomas: languages.filter((language) => language.nome)
      }

      /** Se for absoluto, remover o baseRoom */
      if (this.isAbsolute) delete data.baseOcupantes

      return data
    },

    /** Criar um quarto */
    createRoom() {
      /** Verificar se há imagens não cortadas */
      if (this.images.filter((item) => item instanceof File).length) {
        return (this.showUncroppedImages = true)
      }
      /** Formatar os dados do quarto */
      const data = this.formattedData()

      this.isLoading = true

      /** Criar um quarto */
      this.$room
        .create(data)
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto criado com sucesso!'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /** Salvar quarto */
    saveRoom() {
      /** Verificar se há imagens não cortadas */
      if (this.images.filter((item) => item instanceof File).length) {
        return (this.showUncroppedImages = true)
      }

      /** Formatar os dados do quarto */
      const data = this.formattedData()

      this.isLoading = true

      /** Criar um quarto */
      this.$room
        .update({ ...data, codigo: this.roomId })
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto salvo com sucesso!'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    /** Buscar amenidades */
    async getAmenities() {
      await this.$amenities
        .getAmenities()
        .then((response) => {
          return (this.amenities = response.data.map((item) => {
            const obj = {
              id: item.codigo,
              text: item.idiomas
            }
            return obj
          }))
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar as amenidades.'
          })
        })
    },

    /** Desativar quartos */
    inactiveRooms() {
      this.$room
        .inative(parseInt(this.hotelId), [this.roomId])
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto desativado com sucesso.'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .catch(() => {
          this.showAlertModalInactivate = false
          this.showAlertModalRoomCantDelete = true
        })
    },

    /** Ativar quartos */
    activeRooms() {
      this.$room
        .active(parseInt(this.hotelId), [this.roomId])
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Quarto ativado com sucesso.'
          })
          /** Redirecionar para a listagem de quartos */
          setTimeout(() => {
            this.$router.push({
              path: `/hoteis/quartos/${this.hotelId}`
            })
          }, 1000)
        })
        .catch(() => {
          this.showAlertModalActivate = false
          this.showAlertModalRoomCantDelete = true
        })
    },

    /** Salvar imagem da galeria
     * @param {Object} data - Objeto com a imagem e a posição na galeria
     */
    saveImageGallery(image) {
      const data = new FormData()
      data.append('imagem', image.file)
      this.$room
        .image(data)
        .then((response) => {
          /** Remover imagem anterior */
          this.images.splice(image.position, 1)

          /** Adicionar a imagem nova na posição */
          this.images.splice(image.position, 0, response.data.url)

          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Imagem redimensionada com sucesso!'
          })
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
          /** Limpar o campo de imagem */
          this.images[image.position] = null
        })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}

b {
  font-weight: 500;
}

.link {
  color: pmw-colors('blue-100') !important;
  cursor: pointer;
}

.tab-title {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sidebar {
  &--title {
    margin-bottom: 15px;
  }
  &--button {
    margin-bottom: 10px;
  }
  &--text {
    line-height: 21px;
  }
}

.tabs {
  ::v-deep {
    .tabs--head {
      margin: 0px;
    }
    .tabs--content {
      padding: 30px 0px 0px 0px;
    }
  }
}

.pmw-form-field {
  ::v-deep {
    .pmw-custom-tooltip {
      min-width: 215px;
      line-height: 18px;
    }
  }
}

.form-name-row {
  display: flex;
  align-items: flex-end;
  gap: 24px;
  margin-bottom: 24px;

  .form-name {
    max-width: 424px;
    margin-bottom: 0;
    flex-shrink: 0;
  }
}

.form-name-checkbox {
  padding-bottom: 8px;
  white-space: nowrap;

  ::v-deep .input-checkbox--label__overflow {
    overflow: visible;
    text-overflow: unset;
  }
}

.form-desc {
  max-width: 697px;

  ::v-deep {
    .pmw-textarea--label {
      margin: 0px;
    }
    .pmw-textarea--field {
      line-height: 20px;
    }
  }
}

.form-base-value {
  max-width: 160px;
}

.title {
  margin-bottom: 30px;
}

.form-grid {
  display: flex;
  gap: 20px;

  &--item {
    &__base-room {
      max-width: 160px;
    }

    &__min-adults {
      max-width: 135px;
    }

    &__max-adults {
      max-width: 142px;
    }

    &__max-children,
    &__free-children {
      max-width: 145px;
    }

    &__max-occupants {
      max-width: 160px;
    }
  }

  &--info-icon {
    ::v-deep .pmw-info-icon {
      &:hover {
        color: pmw-colors('error-100');
      }
    }
  }
}

.pmw-selection-box {
  margin: 40px 0px 0px 0px;

  ::v-deep {
    .pmw-separate-line {
      display: none;
    }
    span .pmw-icon {
      display: none;
    }
  }
}

.pmw-image-upload {
  width: 100%;

  ::v-deep {
    .pmw-box-file {
      padding: 30px 50px 60px 50px;
    }
    .pmw-box-file--images {
      padding: 25px auto 50px auto;
    }
    .pmw-form-field--label {
      font-weight: font-weight('weight-light');
      margin-bottom: 6px;
    }
    .pmw-form-field--description {
      font-size: font-size('size-small');
    }
  }
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
